import './EvaluationResultsSavings.scss';
import { CurrencyCode, EvaluationResults, HvacArea } from '@belimo-retrofit-portal/logic';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { CarbonSavingsUnit } from 'src/modules/common/components/CarbonSavingsUnit';
import { CurrencySymbol } from 'src/modules/common/components/CurrencySymbol';
import { FormattedValue } from 'src/modules/common/components/FormattedValue';
import { SavingsAmount } from 'src/modules/common/components/SavingsAmount';
import { SavingsValue } from 'src/modules/common/components/SavingsValue';
import { SavingTileCard } from 'src/modules/common/components/SavingTileCard';
import { TileCard } from 'src/modules/common/components/TileCard';
import { sub, sup } from 'src/modules/common/constants/formattingHelpers';
import { ChartUpIcon } from 'src/modules/common/icons/ChartUpIcon';
import { CloudIcon } from 'src/modules/common/icons/CloudIcon';
import { CurrencyExchangeIcon } from 'src/modules/common/icons/CurrencyExchangeIcon';
import { HeatingIcon } from 'src/modules/common/icons/HeatingIcon';
import { HVAC_AREA_LIST } from 'src/modules/hvac/constants/area';

type Props = {
  readonly results: EvaluationResults;
  readonly currency: CurrencyCode;
};

export const EvaluationResultsSavings = ({ results, currency }: Props): React.ReactElement => (
  <TileCard title={(
    <FormattedMessage
      id="evaluation/savings/title"
      values={{ sub, sup }}
    />
  )}
  >
    <div className="bp-evaluation-results-savings__grid">
      <SavingTileCard
        title={(
          <FormattedMessage
            id="evaluation/savings/energy/title"
            values={{ sub, sup }}
          />
        )}
        icon={<HeatingIcon/>}
        label={(
          <FormattedMessage
            id="evaluation/savings/energy/subtitle"
            values={{ sub, sup }}
          />
        )}
        value={(
          <>
            <SavingsValue
              value={results.savings.energy.overall?.mul(100) ?? null}
              precision={0}
            />

            <FormattedMessage id="unit/percent"/>
          </>
        )}
        details={HVAC_AREA_LIST.map((area) => ({ detailLabel: HVAC_NAMES[area],
          detailValue: (
            <>
              <SavingsValue
                value={results.savings.energy.perArea[area]?.mul(100) ?? null}
                precision={0}
              />
              <FormattedMessage id="unit/percent"/>
            </>
          ) }))}
        bordered={true}
      />

      <SavingTileCard
        title={(
          <FormattedMessage
            id="evaluation/savings/costExclCO2/title"
            values={{ sub, sup }}
          />
        )}
        icon={<CurrencyExchangeIcon/>}
        label={(
          <FormattedMessage
            id="evaluation/savings/costExclCO2/subtitle"
            values={{ sub: sub, sup: sup, currency: <CurrencySymbol currency={currency}/> }}
          />
        )}
        value={(
          <SavingsAmount
            value={results.savings.costExclCO2.overall}
            currency={currency}
          />
        )}
        details={HVAC_AREA_LIST.map((area) => ({
          detailLabel: HVAC_NAMES[area],
          detailValue: <SavingsAmount
            value={results.savings.costExclCO2.perArea[area]}
            currency={currency}
          /> }))}
        bordered={true}
      />

      <SavingTileCard
        title={(
          <FormattedMessage
            id="evaluation/savings/costInclCO2/title"
            values={{ sub, sup }}
          />
        )}
        icon={<CurrencyExchangeIcon/>}
        label={(
          <FormattedMessage
            id="evaluation/savings/costInclCO2/subtitle"
            values={{ sub: sub, sup: sup, currency: <CurrencySymbol currency={currency}/> }}
          />
        )}
        value={(
          <SavingsAmount
            value={results.savings.costInclCO2.overall}
            currency={currency}
          />
        )}
        details={HVAC_AREA_LIST.map((area) => ({
          detailLabel: HVAC_NAMES[area],
          detailValue: <SavingsAmount
            value={results.savings.costInclCO2.perArea[area]}
            currency={currency}
          /> }))}
        bordered={true}
      />
    </div>

    <div className="bp-evaluation-results-savings__grid">
      <SavingTileCard
        title={(
          <FormattedMessage
            id="evaluation/investment/title"
            values={{ sub, sup }}
          />
        )}
        icon={<ChartUpIcon/>}
        label={(
          <FormattedMessage
            id="evaluation/investment/subtitle"
            values={{ sub: sub, sup: sup, currency: <CurrencySymbol currency={currency}/> }}
          />
        )}
        value={(
          <>
            <FormattedValue
              value={results.investment.carbonEmission}
              precision={0}
            />
            <FormattedMessage
              id="evaluation/investment/unit"
              values={{ sub: sub, sup: sup, currency: <CurrencySymbol currency={currency}/> }}
            />
          </>
        )}
        details={[{
          detailLabel: <FormattedMessage
            id="evaluation/investment/description/title"
            values={{ sub, sup }}
          />,
          detailValue: (
            <span>
              <FormattedMessage
                id="evaluation/investment/description/text"
                values={{ sub, sup }}
              />
            </span>
          ),
        }]}
        bordered={true}
      />

      <SavingTileCard
        title={(
          <FormattedMessage
            id="evaluation/savings/carbon/title"
            values={{ sub, sup }}
          />
        )}
        icon={<CloudIcon/>}
        label={(
          <FormattedMessage
            id="evaluation/savings/carbon/subtitle"
            values={{ sub, sup }}
          />
        )}
        value={(
          <>
            <SavingsValue
              value={results.savings.carbon.overall?.div(1_000_000) ?? null}
              precision={0}
            />
            <CarbonSavingsUnit/>
          </>
        )}
        details={HVAC_AREA_LIST.map((area) => ({
          detailLabel: HVAC_NAMES[area],
          detailValue: (
            <>
              <SavingsValue
                value={results.savings.carbon.perArea[area]?.div(1_000_000) ?? null}
                precision={0}
              />
              <CarbonSavingsUnit/>
            </>
          ) }
        ))}
        bordered={true}
      />
    </div>
  </TileCard>
);

const HVAC_NAMES = {
  [HvacArea.HEATING]: <FormattedMessage id="evaluation/hvacArea/heating"/>,
  [HvacArea.COOLING]: <FormattedMessage id="evaluation/hvacArea/cooling"/>,
  [HvacArea.VENTILATION]: <FormattedMessage id="evaluation/hvacArea/ventilation"/>,
};
